import {
  mapPurchaseOrderPrintSettingsToUI,
  PURCHASE_ORDER_PRINT_DEFAULTS,
  type AppConfigRow,
  type InventorySupplierRow,
  type PurchaseOrderPrintLogoSource,
  type PurchaseOrderPrintSettingsRow,
  type PurchaseOrderPrintSettingsUI,
} from '@helios/data-access';
import { orgLogoUrl, orgLogoHorizontalUrl } from '../../../shell/shared';
import { makePrintStyles, PAGE_STYLES } from '../prescription-print/print-utils';
import type {
  PurchaseOrderPrintBuyer,
  PurchaseOrderPrintSettings,
  PurchaseOrderPrintSupplier,
} from './PurchaseOrderPrintLayout';

/**
 * PRD.239 — Scoped print stylesheet for the Purchase Order print
 * surface. Reuses the shared `body[data-print-target=…]` scoping
 * pattern from prescription print (PRD.184) so PO print can co-exist
 * with invoice print / prescription print mounts on the same page
 * without `@media print` rules clobbering each other.
 *
 * Target: `purchase-order` · mount class: `po-print-only`
 */
export const PURCHASE_ORDER_PRINT_STYLES = `${PAGE_STYLES}\n${makePrintStyles(
  'purchase-order',
  'po-print-only',
)}`;

/**
 * PRD.239 — Build the effective settings UI from the saved
 * `purchase_order_print_settings` row, falling back to data-access
 * defaults when the wizard has never been saved. Identical pattern to
 * `resolveInvoicePrintSettings`. Only the row → UI mapping is touched;
 * no DB writes here.
 */
export function resolvePurchaseOrderSettings(
  savedRow: PurchaseOrderPrintSettingsRow | null,
): PurchaseOrderPrintSettingsUI {
  if (savedRow) return mapPurchaseOrderPrintSettingsToUI(savedRow);

  const defaultLogoSource: PurchaseOrderPrintLogoSource = orgLogoUrl
    ? 'standard'
    : orgLogoHorizontalUrl
      ? 'horizontal'
      : 'none';

  return {
    id: '',
    ...PURCHASE_ORDER_PRINT_DEFAULTS,
    logoSource: defaultLogoSource,
  };
}

/**
 * Project the settings UI into the buyer-facing block. Saved buyer
 * overrides win; otherwise fall back to `app_config` branding so a
 * clinic that has only configured app_config still gets a usable
 * printout.
 */
export function resolveBuyer(
  settings: PurchaseOrderPrintSettingsUI,
  appConfig: AppConfigRow | null,
): PurchaseOrderPrintBuyer {
  return {
    name: settings.buyerName.trim() || appConfig?.org_name?.trim() || 'Buyer Organization',
    address: settings.buyerAddress.trim() || appConfig?.org_address || '',
    phone: settings.buyerPhone.trim() || appConfig?.org_phone || '',
    email: settings.buyerEmail.trim() || appConfig?.org_email || '',
    taxId: settings.buyerTaxId.trim() || '',
  };
}

/**
 * Build the supplier block. Snapshot-first per PRD.239 §12 — the
 * historical supplier name on the PO is the display source of truth.
 * Contact details from the live supplier master are added only as
 * helpful context and never override the snapshotted name.
 */
export function resolveSupplier(
  poSupplierNameSnapshot: string | null,
  supplierMaster: InventorySupplierRow | null,
): PurchaseOrderPrintSupplier {
  const snapshotName = poSupplierNameSnapshot?.trim() || '';
  const masterName = supplierMaster?.supplier_name?.trim() || '';
  return {
    name: snapshotName || masterName || '—',
    contactPerson: supplierMaster?.contact_person ?? '',
    address: supplierMaster?.address ?? '',
    phone: supplierMaster?.phone ?? '',
    email: supplierMaster?.email ?? '',
    taxId: supplierMaster?.tax_id ?? '',
    paymentTerms: supplierMaster?.payment_terms ?? '',
  };
}

/**
 * Project the settings UI into the shape the print layout component
 * consumes. The layout only needs the rendering subset (toggles +
 * labels + footer/terms text + logo); buyer fields are passed through
 * a separate `buyer` prop, which the resolver above produces.
 */
export function toLayoutSettings(ui: PurchaseOrderPrintSettingsUI): PurchaseOrderPrintSettings {
  return {
    logoSource: ui.logoSource,
    showSku: ui.showSku,
    showBonus: ui.showBonus,
    showRemarks: ui.showRemarks,
    showSupplierContact: ui.showSupplierContact,
    showSignatureBlock: ui.showSignatureBlock,
    showTerms: ui.showTerms,
    footerText: ui.footerText,
    termsText: ui.termsText,
    preparedByLabel: ui.preparedByLabel,
    approvedByLabel: ui.approvedByLabel,
    supplierAcknowledgmentLabel: ui.supplierAcknowledgmentLabel,
  };
}
